import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Loader } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { BGLoadingIndicator } from '../BGLoadingIndicator/BGLoadingIndicator';
import { getKnowledgeDoc, getMessageContent, downloadKnowledgeDoc } from '../../services/knowledgeDocsService';
import { formatKnowledgeDocTitle, parseKnowledgeDocs, KnowledgeDoc } from '../../utils/parseKnowledgeDocs';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { IKnowledgeDocDetailProps } from './KnowledgeDocDetail.type';

// Side panel for a knowledge document — the in-place counterpart to ArtifactDetail.
// Mirrors that component's header/layout, but renders Markdown only (knowledge docs are
// always text/markdown) and resolves content via the knowledge-docs service.
export const KnowledgeDocDetail: React.FC<IKnowledgeDocDetailProps> = ({ doc, isOpen, onClose, permissions }) => {
  const [content, setContent] = useState<string>('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { projectId } = useParams<{ projectId: string }>();

  // Stable key so we only refetch when the actual doc changes, not on every parent re-render.
  const docKey = doc ? `${doc.docId || ''}-${doc.identifier || ''}-${doc.messageId || ''}` : null;

  useEffect(() => {
    let cancelled = false;

    const resolveContent = async () => {
      if (!doc || !isOpen) {
        setContent('');
        return;
      }

      // In-memory content (the chat card already has it) — render directly.
      if (doc.content && doc.content.trim()) {
        setContent(doc.content);
        return;
      }

      if (!projectId) return;

      try {
        setLoadingContent(true);
        if (doc.docId) {
          // Saved: fetch by id (also stamps last_accessed_at server-side).
          const record = await getKnowledgeDoc(projectId, doc.docId);
          if (!cancelled) setContent(record.content || '');
        } else if (doc.messageId && doc.identifier) {
          // Unsaved: pull the source message and extract the matching block.
          const { content: msgContent } = await getMessageContent(projectId, doc.messageId);
          const { parts } = parseKnowledgeDocs(msgContent);
          const match = parts.find(
            (p): p is KnowledgeDoc => typeof p !== 'string' && p.identifier === doc.identifier,
          );
          if (!cancelled) setContent(match?.content || '');
        } else if (!cancelled) {
          setContent('');
        }
      } catch (error) {
        console.error('Failed to load knowledge document content:', error);
        if (!cancelled) setContent('');
      } finally {
        if (!cancelled) setLoadingContent(false);
      }
    };

    resolveContent();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey, isOpen]);

  // Always goes through the download endpoint so the server can refuse it; a saved doc is
  // served from the database, an unsaved one from the content resolved above.
  const handleDownload = async () => {
    if (!doc || !projectId) return;
    setIsDownloading(true);
    try {
      await downloadKnowledgeDoc({ projectId, title: doc.title, content, docId: doc.docId });
    } catch (error) {
      console.error('Failed to download knowledge document:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (!projectId || !doc) return;
    const url = doc.docId
      ? `/project/${projectId}/knowledge-doc/${doc.docId}`
      : doc.messageId && doc.identifier
        ? `/project/${projectId}/message/${doc.messageId}/knowledge-doc/${doc.identifier}`
        : null;
    if (!url) return;
    window.open(url, '_blank');
  };

  if (!isOpen) return null;

  if (loadingContent || !doc) {
    return (
      <div className="flex flex-col h-full w-full bg-bg-primary border-l border-border-primary">
        <div className="flex items-center justify-between px-bg-v2-lg py-bg-v2-lg bg-bg-primary border-b border-border-primary h-[60px] relative">
          <div className="flex items-center gap-3">
            <Icon name={IconName.Document} size="md" className="text-fg-secondary" inheritColor />
            <h2 className="text-sm font-semibold text-text-primary">Loading...</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-secondary transition-all duration-200"
            title="Close"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center bg-bg-primary">
          <BGLoadingIndicator size="md" />
        </div>
      </div>
    );
  }

  const displayTitle = formatKnowledgeDocTitle(doc.title, doc.version);
  const canOpenInNewTab = Boolean(doc.docId || (doc.messageId && doc.identifier));

  return (
    <div className="flex flex-col h-full w-full bg-bg-primary border-l border-border-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-bg-v2-lg py-bg-v2-lg bg-bg-primary border-b border-border-primary h-[60px] relative">
        <div className="flex items-center gap-3 min-w-0">
          <Icon name={IconName.Document} size="md" className="text-fg-secondary" inheritColor />
          <h2 className="text-sm font-semibold text-text-primary truncate" title={displayTitle}>
            {displayTitle}
          </h2>
        </div>

        {/* Right Section - Action Icons */}
        <div className="flex items-center gap-0 flex-shrink-0">
          {/* Download */}
          <IfHasPermission permissions={permissions} requires={Permissions.DownloadPermission}>
            {!isDownloading ? (
              <button
                onClick={handleDownload}
                className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-colors"
                title="Download"
              >
                <Icon name={IconName.Download} size="md" inheritColor />
              </button>
            ) : (
              <div className="w-8 h-8 flex items-center justify-center">
                <Loader className="animate-spin h-4 w-4 text-fg-secondary" />
              </div>
            )}
          </IfHasPermission>

          {/* Open in new tab */}
          {canOpenInNewTab && (
            <button
              onClick={handleOpenInNewTab}
              className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-colors"
              title="Open in new tab"
            >
              <Icon name={IconName.ExternalLink} size="md" inheritColor />
            </button>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200"
            title="Close"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="prose prose-lg max-w-none p-6">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              a: ({ href, children, ...props }: any) => (
                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                  {children}
                </a>
              ),
              code({ className, children, ...props }: any) {
                const isInline = !className?.includes('language-');
                return isInline ? (
                  <code className="bg-bg-secondary px-1 py-0.5 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>
                ) : (
                  <code className="block bg-bg-secondary p-3 rounded text-sm font-mono overflow-x-auto" {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDocDetail;
